<script setup>
import { computed } from 'vue';
import GameImage from '@/components/casino_v2/GameImage.vue';

/**
 * Renders the casino games configured on a widget.
 *
 * GameImage owns the image fallback, the access gating and the navigation, so this
 * component only lays the cards out.
 */
const props = defineProps({
	widget: { type: Object, required: true },
});

const games = computed(() => props.widget.items || []);

/**
 * An item may carry an operator chosen image that overrides the game's own.
 */
const gameFor = (item) => (item.media ? { ...item, image_url: item.media.path } : item);
</script>

<template>
	<div class="marketing-game-grid">
		<div v-for="item in games" :key="item.id" class="marketing-game-card">
			<div class="marketing-game-thumb">
				<GameImage :game="gameFor(item)" size="extra-small" aspect_ratio="wide" />
			</div>
			<div class="marketing-game-name">{{ item.name }}</div>
			<div v-if="item.provider_name" class="marketing-game-provider">{{ item.provider_name }}</div>
		</div>
	</div>
</template>

<style scoped>
/*
 * The placement is a narrow column on some pages and the full width on others, so
 * cards per row follow the available width. The floor is the theme's own card width
 * in the narrowest of those columns.
 */
.marketing-game-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(130px, 100%), 1fr));
	gap: 10px;
	width: 100%;
}
.marketing-game-card {
	min-width: 0;
	cursor: pointer;
}
/*
 * GameImage sizes itself from an aspect ratio and, left in the flow, resolves its
 * height against the row and squeezes out the caption. A box owning the ratio keeps
 * it out of the flow.
 */
.marketing-game-thumb {
	position: relative;
	width: 100%;
	aspect-ratio: 4 / 3;
	overflow: hidden;
	border-radius: var(--rounded-md, 6px);
}
.marketing-game-thumb :deep(.image-container) {
	position: absolute;
	inset: 0;
	height: 100%;
	aspect-ratio: auto;
}
.marketing-game-name {
	margin-top: 6px;
	font-size: 12px;
	line-height: 1.3;
	font-weight: 500;
	color: var(--white, #fff);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.marketing-game-provider {
	font-size: 10px;
	line-height: 1.3;
	color: var(--text-secondary-color, #9a9a9a);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
